<template>
	<RouterView />
</template>

<script setup lang="ts">
import { provide, watch } from "vue";
import { BackendType } from "./enums/BackendType";
import { useLoadingStore } from "./store/useLoadingStore";
import { useUserStore } from "./store/useUserStore";
import { onKeyStroke } from "@vueuse/core";

import "./style/main.scss";
import "floating-vue/dist/style.css";
import {
	refreshZeroAuth,
	useZero,
	useConnectionState,
} from "@/composables/useZero";
import { useRecorderStore } from "./store/useRecorderStore";
import { queries } from "@loanlink-nl/zero";

const showValues = import.meta.env.VITE_SHOW_MODEL_VALUES === "true";
const showDevLabels = import.meta.env.VITE_SHOW_DEV_LABELS === "true";

const appModeIsDev = import.meta.env.DEV;
const backendType: BackendType =
	import.meta.env.VITE_USE_PROD_BACKEND !== "true" && appModeIsDev
		? BackendType.Development
		: import.meta.env.VITE_STAGE === "true"
			? BackendType.Stage
			: BackendType.Production;

const state = useConnectionState();
const userStore = useUserStore();
const z = useZero();
const maxZeroAuthRefreshAttempts = 3;
let zeroAuthRefreshAttempts = 0;

watch(state, async (newState) => {
	if (newState.name === "connected") {
		zeroAuthRefreshAttempts = 0;
		return;
	}

	if (newState.name !== "needs-auth") {
		return;
	}

	if (zeroAuthRefreshAttempts >= maxZeroAuthRefreshAttempts) {
		await userStore.logout();
		return;
	}

	zeroAuthRefreshAttempts += 1;

	try {
		const didRefresh = await refreshZeroAuth(z.value);
		if (didRefresh) {
			return;
		}
	} catch {}

	await userStore.logout();
});

watch(
	z,
	async (zero) => {
		if (zero) {
			try {
				// This makes sure that zero is always used in the app, so we won't get a sudden reload on pages it's used.
				zero.preload(queries.preload());
				const { complete } = zero.preload(queries.providers.preload());
				await complete;

				const lenders = await zero.run(queries.lenders.all());
				const serviceProviders = await zero.run(queries.serviceProviders.all());
				for (const entry of [...lenders, ...serviceProviders]) {
					if (!entry.icon) {
						continue;
					}

					const image = new Image();
					image.src = `/logos/${entry.icon}`;
				}
			} catch (error) {
				if (state.value.name === "needs-auth") {
					return;
				}

				throw error;
			}
		}
	},
	{ immediate: true },
);

provide("DEBUG_SHOW_MODELVALUES", showValues);
provide("SHOW_DEV_LABELS", showDevLabels);
provide("BACKEND_TYPE", backendType);

window.addEventListener("beforeunload", onLeave);

onKeyStroke("Escape", () => {
	if (document.activeElement instanceof HTMLElement) {
		document.activeElement.blur();
	}
});

const loadingStore = useLoadingStore();
const recorderStore = useRecorderStore();

/**
 * Presents a warning to the user when the page is unloaded and there are still unsaved changes.
 * Based on https://stackoverflow.com/a/7317311/9487833
 *
 * @param e - The event generated by the beforeunload listener.
 */
function onLeave(e: BeforeUnloadEvent) {
	let confirmationMessage;

	if (loadingStore.unsavedChanges) {
		confirmationMessage =
			"Er zijn onopgeslagen wijzigingen in het actieve dossier, die verloren gaan als je deze pagina verlaat. Wilt je de pagina verlaten?";
	}

	if (recorderStore.recording) {
		recorderStore.save();
	}

	if (confirmationMessage) {
		(e || window.event).returnValue = confirmationMessage; //Gecko + IE
		return confirmationMessage; //Gecko + Webkit, Safari, Chrome etc.
	}
}
</script>
